import axios from "axios";
import moment from "moment-timezone";

import { API_URL } from "../env";

/**
 * Shared token refresh manager to prevent race conditions
 * when both api/common.js and apiv2/common.js need to refresh tokens
 */

let isRefreshing = false;
let failedQueue = [];

/**
 * Process all queued requests after token refresh completes
 */
export const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Add a request to the queue while token refresh is in progress
 */
export const enqueueRequest = () => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
};

/**
 * Safely retrieve the auth token from localStorage
 * @returns {Object|null} The parsed auth token object, or null if unavailable/invalid
 */
export const getAuthToken = () => {
  try {
    const authData = window.localStorage.getItem("auth");
    if (!authData) {
      return null;
    }
    return JSON.parse(authData);
  } catch (parseError) {
    console.error("Failed to parse auth data from localStorage:", parseError);
    return null;
  }
};

/**
 * Check if a token refresh is currently in progress
 */
export const getIsRefreshing = () => isRefreshing;

/**
 * Set the refresh state
 */
export const setIsRefreshing = (value) => {
  isRefreshing = value;
};

/**
 * Returns true when the refresh failure is a definitive auth rejection
 * (the token is permanently invalid) rather than a transient problem
 * (network blip, server error) that may resolve on its own.
 */
export const isDefinitiveAuthFailure = (error) => {
  const status = error?.response?.status;
  return status === 400 || status === 401;
};

/**
 * Core refresh logic, separated so it can be called directly or wrapped
 * with the Web Locks API for cross-tab coordination.
 */
const performRefresh = async (originalAccessToken) => {
  const storedAuth = localStorage.getItem("auth");

  // No stored session is an ordinary outcome, not a fault: the user is logged
  // out, or another tab logged out while this call waited on the cross-tab
  // lock. This check is deliberately OUTSIDE the try below, which used to
  // catch this very throw and relabel it "Invalid auth data" - reporting
  // absent data as corrupt data. Callers treat a throw as a non-definitive
  // failure, so the pending request rejects without tearing down a session
  // that has already ended.
  if (!storedAuth) {
    throw new Error("No session to refresh");
  }

  let token;
  try {
    token = JSON.parse(storedAuth);
  } catch (parseError) {
    // Genuinely corrupt: drop it so the next load starts from a clean slate.
    localStorage.removeItem("auth");
    console.error("Failed to parse auth data from localStorage:", parseError);
    throw new Error("Invalid auth data");
  }

  if (!token || !token.refresh_token) {
    throw new Error("No refresh token available");
  }

  // If another tab already refreshed while we waited for the lock, the stored
  // access_token will differ from the one that triggered this refresh. Return
  // the already-refreshed data instead of burning the rotated refresh token.
  if (originalAccessToken && token.access_token !== originalAccessToken) {
    return token;
  }

  const data = {
    grant_type: "refresh_token",
    refresh_token: token.refresh_token,
  };

  // Use URLSearchParams for proper application/x-www-form-urlencoded encoding
  const params = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    params.append(key, data[key]);
  });

  // Add skipAuthRefresh flag to prevent interceptor from catching this request
  const response = await axios.post(`${API_URL}token/`, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    skipAuthRefresh: true,
  });

  // Preserve the original time_sign_up from existing auth data
  let existingTimeSignUp;
  try {
    const existingAuthData = window.localStorage.getItem("auth");
    if (existingAuthData) {
      const existingAuth = JSON.parse(existingAuthData);
      existingTimeSignUp = existingAuth.time_sign_up;
    }
  } catch (parseError) {
    // If we can't parse existing auth, we'll fall back to setting a new timestamp
    console.warn("Could not parse existing auth data to preserve time_sign_up:", parseError);
  }

  const nowUnix = moment.tz(moment.tz.guess()).unix();
  const authData = {
    ...response.data,
    // Preserve original time_sign_up, or use response value, or set current time as fallback
    time_sign_up: existingTimeSignUp ?? response.data.time_sign_up ?? nowUnix,
    // Absolute expiry so mount-time checks do not depend on time_sign_up drift
    expires_at: response.data.expires_in ? nowUnix + response.data.expires_in : undefined,
  };
  window.localStorage.setItem("auth", JSON.stringify(authData));

  return authData;
};

/**
 * Perform the token refresh.
 *
 * When the Web Locks API is available (all modern browsers in secure contexts),
 * the refresh is coordinated across tabs so only one tab issues the token
 * request and the others re-use its result. Falls back to in-tab-only refresh
 * when the API is absent.
 */
export const refreshAuthToken = async () => {
  // Capture the access_token that triggered this refresh so we can detect
  // whether another tab already refreshed while we waited for the lock.
  let originalAccessToken;
  try {
    const raw = localStorage.getItem("auth");
    if (raw) {
      originalAccessToken = JSON.parse(raw).access_token;
    }
  } catch {
    // Will be caught again inside performRefresh
  }

  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("factory-auth-refresh", async () => {
      return performRefresh(originalAccessToken);
    });
  }

  return performRefresh(originalAccessToken);
};

/**
 * Handle logout and redirect
 */
export const handleLogout = () => {
  window.localStorage.removeItem("auth");
  window.localStorage.removeItem("user");
  window.location.href = "/login";
};
